import { AlertCircle, AlertTriangle, CheckCircle, Search, Users, Video } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { FormInput, FormSelect, FormTextArea } from '../forms';

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00',
  '14:00', '15:00', '16:00'
];

const ClientSearch = ({ onSelect, onClose }) => {
  const { get } = useApi();
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const searchClients = async () => {
      if (query.length < 2) return;
      try {
        setLoading(true);
        const response = await get(`/users/search?query=${query}&role=student`);
        setClients(response.data.data || []);
      } catch (error) {
        console.error('Failed to search clients:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchClients, 300);
    return () => clearTimeout(debounce);
  }, [query, get]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Search Client</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 p-2 rounded-full hover:bg-gray-100"
          >
            ×
          </button>
        </div>
        <div className="p-6">
          <div className="relative mb-4">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
              placeholder="Search by name or ID..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-uzima-maroon-500 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Searching...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No clients found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {clients.map(client => (
                  <button
                    key={client._id}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg border border-transparent hover:border-gray-200 transition-all duration-200"
                    onClick={() => onSelect(client)}
                  >
                    <div className="font-medium text-gray-900">{client.fullName}</div>
                    <div className="text-sm text-gray-500 mt-1">{client.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BookAppointment = ({ onClose, isUrgent: initialUrgent = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [counselors, setCounselors] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isUrgent, setIsUrgent] = useState(initialUrgent);

  const [formData, setFormData] = useState({
    counselorId: '',
    sessionType: '',
    serviceType: '',
    date: '',
    timeSlot: '',
    clientId: user.role === 'student' ? user.id : '',
    urgencyReason: '',
    notes: ''
  });

  const [blockedDates, setBlockedDates] = useState([])

  const isDateBlocked = (date) => {
    const dayEvents = blockedDates.find(blockedDate => blockedDate.date === date);
    return dayEvents && dayEvents.events.length > 0;
  };

  const isTimeSlotBlocked = (date, timeSlot) => {
    // Assume blockedDates is an array with items like:
    // { date: "2025-02-12", events: [{ startTime: "10:00", endTime: "11:00", type: "event" }] }
    const dayEvents = blockedDates.find(item => item.date === date);
    if (!dayEvents) return false;
    return dayEvents.events.some(event =>
      event.type === 'event' && timeSlot >= event.startTime && timeSlot < event.endTime
    );
  };



  useEffect(() => {
    const fetchBlockedSlots = async () => {
      try {
        // Fetch individual blocked time slots
        const responseSlots = await get('/blocked-slots');
        // Fetch recurring blocked slots
        const responseRecurring = await get('/recurring-blocks');

        if (responseSlots.data?.status === 'success' && responseRecurring.data?.status === 'success') {
          // Format the blocked slots data
          const formattedSlots = responseSlots.data.data.map(item => ({
            date: item.date.split('T')[0],
            events: [{ startTime: item.startTime, endTime: item.endTime, type: item.type }]
          }));

          // Format the recurring blocks data
          const formattedRecurring = responseRecurring.data.data.map(item => ({
            recurrenceRule: item.recurrenceRule, // Example: "Every Monday 9AM-12PM"
            events: [{ startTime: item.startTime, endTime: item.endTime, type: item.type }]
          }));

          // Update state with both regular and recurring blocks
          setBlockedDates([...formattedSlots, ...formattedRecurring]);
        }
      } catch (err) {
        console.error('Failed to fetch blocked slots:', err);
      }
    };

    fetchBlockedSlots();
  }, [get]); // Dependency array ensures it runs when `get` changes


  useEffect(() => {
    const fetchCounselors = async () => {
      try {
        const response = await get('/users/counselors');
        if (response.data?.status === 'success') {
          setCounselors(response.data.data);
        }
      } catch (err) {
        setError('Failed to load counselors');
      }
    };

    fetchCounselors();
  }, [get]);

  // useEffect(() => {
  //   const fetchAssignedCounselor = async () => {
  //     if (user.role === 'student') {
  //       try {
  //         const response = await get(`/users/${user.id}/assigned-counselor`);
  //         if (response.data?.status === 'success' && response.data.data) {
  //           setFormData(prev => ({
  //             ...prev,
  //             counselorId: response.data.data._id
  //           }));
  //         }
  //       } catch (error) {
  //         console.error('Failed to fetch assigned counselor:', error);
  //       }
  //     }
  //   };

  //   fetchAssignedCounselor();
  // }, [user.id, user.role, get]);
  useEffect(() => {
    const fetchAssignedCounselor = async () => {
      if (user && user.role === 'student' && user.id) {
        try {
          const response = await get(`/users/${user.id}/assigned-counselor`);
          if (response.data?.status === 'success' && response.data.data) {
            // Existing client with assigned counselor
            setFormData(prev => ({
              ...prev,
              counselorId: response.data.data._id
            }));
          } else {
            // New client with no assigned counselor
            setFormData(prev => ({
              ...prev,
              counselorId: '' // Clear this to allow selection
            }));
          }
        } catch (error) {
          console.error('Failed to fetch assigned counselor:', error);
          // Set counselorId to empty to enable dropdown selection
          setFormData(prev => ({
            ...prev,
            counselorId: ''
          }));
        }
      }
    };

    if (user && user.id) {
      fetchAssignedCounselor();
    }
  }, [user, get]);

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!formData.counselorId || !formData.date) return;

      try {
        setLoading(true);
        const response = await get('/appointments/available-slots', {
          params: { counselorId: formData.counselorId, date: formData.date }
        });

        if (response.data?.status === 'success') {
          const fetchedSlots = response.data.data.slots || TIME_SLOTS;
          const filteredSlots = fetchedSlots.filter(slot => !isTimeSlotBlocked(formData.date, slot));

          // Handle blocked reason if it exists
          if (response.data.data.blockedReason) {
            setError(response.data.data.blockedReason);
            setAvailableSlots([]);
          } else if (filteredSlots.length === 0) {
            setError('No available time slots for this date. Please choose another.');
            setAvailableSlots([]);
          } else {
            setError(''); // Clear any previous error messages
            setAvailableSlots(filteredSlots);
          }
        }
      } catch (err) {
        console.error('Error fetching available slots:', err);
        // More specific error handling based on the error type
        if (err.response?.status === 400) {
          setError(err.response.data.message || 'Invalid request. Please try again.');
        } else if (err.response?.status === 404) {
          setError('No schedule found for this date.');
        } else {
          setError('This date is reserved as a holiday. Please select another date');
        }
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableSlots();
  }, [formData.counselorId, formData.date, get]);
  // useEffect(() => {
  //   const fetchAvailableSlots = async () => {
  //     if (!formData.counselorId || !formData.date) return;

  //     try {
  //       setLoading(true);
  //       const response = await get('/appointments/available-slots', {
  //         params: { counselorId: formData.counselorId, date: formData.date }
  //       });

  //       if (response.data?.status === 'success') {
  //         const fetchedSlots = response.data.data.slots || TIME_SLOTS;
  //         const filteredSlots = fetchedSlots.filter(slot => !isTimeSlotBlocked(formData.date, slot));

  //         if (filteredSlots.length === 0) {
  //           setError('No available time slots for this date. Please choose another.');
  //         } else {
  //           setError(''); // Clear any previous error messages
  //         }

  //         setAvailableSlots(filteredSlots);
  //       }
  //     } catch (err) {
  //       setError('This date is reserved as a holiday. Please select another date');
  //       setAvailableSlots(TIME_SLOTS);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchAvailableSlots();
  // }, [formData.counselorId, formData.date, get]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const endpoint = isUrgent ? '/appointments/urgent' : '/appointments';
      const response = await post(endpoint, {
        ...formData,
        clientId: user.role === 'student' ? user.id : selectedClient?._id
      });

      if (response.data?.status === 'success') {
        setSuccess(true);
        // Delay navigation to show success message
        setTimeout(() => {
          const basePath = `/${user.role}`;
          if (onClose) {
            onClose();
            navigate(`${basePath}/appointments`);
          } else {
            navigate(`${basePath}/appointments/${response.data.data._id}`);
          }
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, clientId: client._id }));
    setShowClientSearch(false);
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 mb-2">
            Appointment Booked Successfully
          </h2>
          <p className="text-gray-600">
            Redirecting you to the appointment details...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-medium text-gray-900">
            {isUrgent ? 'Book Urgent Appointment' : 'Book Appointment'}
          </h2>
          {user.role === 'receptionist' && (
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isUrgent}
                onChange={(e) => setIsUrgent(e.target.checked)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-red-600">Urgent Case</span>
            </label>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
          >
            ×
          </button>
        )}
      </div>

      {error && (
        <div className="px-6 pt-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {user.role === 'receptionist' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">Client</label>
                    <button
                      type="button"
                      onClick={() => setShowClientSearch(true)}
                      className="text-sm text-uzima-maroon-500 hover:text-uzima-maroon-400"
                    >
                      Search Client
                    </button>
                  </div>
                  {selectedClient ? (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="font-medium text-gray-900">{selectedClient.fullName}</div>
                      <div className="text-sm text-gray-500 mt-1">{selectedClient.email}</div>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-gray-500 text-sm">
                      No client selected
                    </div>
                  )}
                </div>
              )}

              <FormSelect
                label="Session Type"
                name="sessionType"
                value={formData.sessionType}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  sessionType: e.target.value
                }))}
                options={[
                  { value: 'physical', label: 'Physical' },
                  { value: 'virtual', label: 'Virtual' },
                ]}
                required
              />

              {/* Virtual Session Info - Compact Version */}
              {formData.sessionType === 'virtual' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <Video className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-blue-900 mb-1">Virtual Session via Jitsi Meet</h3>
                      <p className="text-xs text-blue-700 leading-relaxed">
                        A secure meeting link will be created automatically. You can join 15 minutes before your scheduled time.
                        The link will be available in your appointment details.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <FormSelect
                label="Service Type"
                name="serviceType"
                value={formData.serviceType}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  serviceType: e.target.value
                }))}
                options={[
                  { value: 'personal-counseling', label: 'Personal Counseling' },
                  { value: 'vct', label: 'VCT' },
                  { value: 'consultation', label: 'Consultation' },
                  { value: 'group-therapy', label: 'Group Therapy' }
                ]}
                required
              />
              <FormSelect
                label="Counselor"
                name="counselorId"
                value={formData.counselorId}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  counselorId: e.target.value
                }))}
                options={counselors
                  // More explicit filtering logic
                  .filter(counselor => {
                    if (formData.serviceType !== 'vct') {
                      return true; // Show all counselors for other service types
                    }
                    // For VCT, ensure we're checking with explicit true comparison
                    return counselor.canDoVCT === true;
                  })
                  .map(counselor => ({
                    value: counselor._id,
                    label: counselor.fullName || counselor.username
                  }))}
                disabled={!formData.serviceType}
                required
              />

              {/* <FormSelect
                label="Counselor"
                name="counselorId"
                value={formData.counselorId}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  counselorId: e.target.value
                }))}
                options={counselors.map(counselor => ({
                  value: counselor._id,
                  label: counselor.fullName || counselor.username
                }))}
                required
              /> */}

            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <FormInput
                type="date"
                label="Appointment Date"
                name="date"
                value={formData.date}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  // Remove the alert so that users can choose a date even if there is an event.
                  // Clear the previously selected time slot when the date changes.
                  setFormData((prev) => ({ ...prev, date: selectedDate, timeSlot: '' }));
                }}
                min={new Date().toISOString().split('T')[0]}
                disabled={loading}
                required
              />


              <FormSelect
                label="Time Slot"
                name="timeSlot"
                value={formData.timeSlot}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    timeSlot: e.target.value
                  }))
                }
                options={availableSlots.map((slot) => ({
                  value: slot,
                  label: slot,
                  disabled: isTimeSlotBlocked(formData.date, slot) // Disable blocked time slots only
                }))}
                disabled={!formData.date}
                required
              />
            </div>
          </div>

          {/* Urgent Case Fields */}
          {isUrgent && (
            <div className="space-y-4">
              <FormTextArea
                label="Reason for Urgency"
                value={formData.urgencyReason}
                onChange={(e) => setFormData(prev => ({ ...prev, urgencyReason: e.target.value }))}
                required={isUrgent}
              />
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      Urgent cases may require rescheduling of existing appointments.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <FormTextArea
            label="Additional Notes"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              // onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!formData.date || !formData.timeSlot) ||
                (user.role === 'receptionist' && !selectedClient)}
              className={`px-6 py-2 rounded-md text-white flex items-center ${isUrgent
                ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                : 'bg-uzima-maroon-500 hover:bg-uzima-maroon-600 disabled:bg-blue-300'
                }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  <span>Processing, please wait while we schedule your appointment...</span>
                </>
              ) : (
                <span>{isUrgent ? 'Book Urgent Appointment' : 'Book Appointment'}</span>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Client Search Modal */}
      {showClientSearch && (
        <ClientSearch
          onSelect={handleClientSelect}
          onClose={() => setShowClientSearch(false)}
        />
      )}
    </div>
  );
};

export default BookAppointment;